import React, { useEffect, useRef, useState } from 'react';
import { CharCount } from "./CharCount";
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { X, Mic, BookMarked, Quote, Lightbulb, Bookmark, Repeat, Lock, BookOpen } from 'lucide-react';
import chaptersWithinTranslations from '../i18n/chaptersWithinTranslations';
import { useAuth } from '../context/AuthContext';
import '../styles/ChaptersWithin.css';

const SPEECH_LANG = {
  en: 'en-US', hi: 'hi-IN', te: 'te-IN', ta: 'ta-IN', kn: 'kn-IN',
  mr: 'mr-IN', bn: 'bn-IN', gu: 'gu-IN', ml: 'ml-IN', pa: 'pa-IN',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ChaptersWithinSection({ activeLang = 'en', backendUrl, hideSanctuaryNote = false }) {
  const t = chaptersWithinTranslations[activeLang] || chaptersWithinTranslations.en;
  const { isAuthenticated, user, authHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'sealing' | 'done' | 'savePrompt' | 'capReached'

  // Form fields
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [reflection, setReflection] = useState('');
  const [practice, setPractice] = useState('');
  const [passage, setPassage] = useState('');
  const [revisit, setRevisit] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savedPrivate, setSavedPrivate] = useState(false);
  const [usage, setUsage] = useState(null); // { count, limit, at_cap } for logged-in users

  // Pre-fill name/email when logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setName((prev) => prev || user.name || '');
      setEmail((prev) => prev || user.email || '');
    }
  }, [isAuthenticated, user]);

  // Fetch usage when modal opens for logged-in users
  useEffect(() => {
    if (!open || !isAuthenticated) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/user/usage`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setUsage(data);
      } catch { /* ignored */ }
    })();
    return () => { cancelled = true; };
  }, [open, isAuthenticated, backendUrl, authHeaders]);

  // Voice
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const interimRef = useRef('');
  const baseTextRef = useRef('');
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;

  // Body scroll lock
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch { /* ignored */ }
    };
  }, []);

  const resetState = () => {
    setBookTitle(''); setBookAuthor(''); setReflection('');
    setPractice(''); setPassage(''); setRevisit('');
    setName(isAuthenticated ? (user?.name || '') : '');
    setEmail(isAuthenticated ? (user?.email || '') : '');
    setError('');
    setPhase('form'); setListening(false); setSavedPrivate(false);
    try { recognitionRef.current?.stop(); } catch { /* ignored */ }
  };

  const closeModal = () => {
    setOpen(false);
    setTimeout(resetState, 400);
  };

  // Save draft to localStorage so a Create-account detour doesn't lose typing
  const persistDraft = () => {
    try {
      localStorage.setItem('icraftz_chapter_draft', JSON.stringify({
        bookTitle, bookAuthor, reflection, practice, passage, revisit,
        name, email, activeLang, savedAt: Date.now(),
      }));
    } catch { /* ignored */ }
  };

  // Hydrate draft (e.g., after returning from /signup → /login)
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem('icraftz_chapter_draft');
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d || Date.now() - (d.savedAt || 0) > 1000 * 60 * 60 * 24) {
        localStorage.removeItem('icraftz_chapter_draft');
        return;
      }
      if (d.bookTitle && !bookTitle) setBookTitle(d.bookTitle);
      if (d.bookAuthor && !bookAuthor) setBookAuthor(d.bookAuthor);
      if (d.reflection && !reflection) setReflection(d.reflection);
      if (d.practice && !practice) setPractice(d.practice);
      if (d.passage && !passage) setPassage(d.passage);
      if (d.revisit && !revisit) setRevisit(d.revisit);
      // After successful hydrate, clear so it doesn't keep re-applying
      localStorage.removeItem('icraftz_chapter_draft');
    } catch { /* ignored */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleListening = () => {
    if (!SpeechRecognition) {
      setError(t.modal.step2.cards.reflection.micUnsupported);
      return;
    }
    setError('');
    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* ignored */ }
      setListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = SPEECH_LANG[activeLang] || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    baseTextRef.current = reflection ? reflection.trim() + ' ' : '';
    interimRef.current = '';
    rec.onresult = (ev) => {
      let interim = '';
      let finalText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const transcript = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += transcript + ' ';
        else interim += transcript;
      }
      if (finalText) {
        baseTextRef.current = (baseTextRef.current + finalText).replace(/\s+/g, ' ');
        interimRef.current = '';
      } else {
        interimRef.current = interim;
      }
      setReflection((baseTextRef.current + interimRef.current).trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try { rec.start(); setListening(true); } catch (_) { setListening(false); }
  };

  const validate = () => {
    if (!bookTitle.trim() || !reflection.trim() || !name.trim() || !email.trim()) {
      setError(t.modal.validationGeneric);
      return false;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError(t.modal.validationEmail);
      return false;
    }
    return true;
  };

  // When the user clicks "Seal & Send":
  //  - Logged in → submit immediately (private save).
  //  - Anonymous + valid form → show "Save it forever?" prompt (one tap to Sign in / Create account / Continue without saving).
  const handlePrimary = () => {
    setError('');
    if (!validate()) return;
    if (isAuthenticated) return doSubmit();
    setPhase('savePrompt');
  };

  const doSubmit = async () => {
    setSubmitting(true);
    try { recognitionRef.current?.stop(); } catch { /* ignored */ }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) Object.assign(headers, authHeaders());
      const res = await fetch(`${backendUrl}/api/chapters-within`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          book_title: bookTitle.trim(),
          book_author: bookAuthor.trim() || null,
          reflection: reflection.trim(),
          ideas_to_practice: practice.trim() || null,
          passage_to_remember: passage.trim() || null,
          revisit_later: revisit.trim() || null,
          name: name.trim(),
          email: email.trim(),
          language: activeLang,
        }),
      });
      if (res.status === 402) {
        setPhase('capReached');
        return;
      }
      if (!res.ok) throw new Error('failed');
      const data = await res.json().catch(() => ({}));
      setSavedPrivate(Boolean(data?.private));
      // Let the My Sanctuary shelf refresh without a reload.
      window.dispatchEvent(new CustomEvent('icraftz:chapter-captured'));
      setPhase('sealing');
      setTimeout(() => setPhase('done'), 850);
    } catch (err) {
      setError(t.modal.submitErrorGeneric);
      setPhase('form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = handlePrimary;

  const cards = t.modal.step2.cards;

  return (
    <>
      <section className="cw-section" id="chapters-within" aria-labelledby="cw-title">
        <div className="cw-section-inner">
          <motion.h2
            id="cw-title"
            className="cw-title"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {(() => {
              const parts = (t.sectionTitle || '').trim().split(' ');
              if (parts.length < 2) return t.sectionTitle;
              const last = parts.pop();
              return (
                <>
                  {parts.join(' ')}{' '}
                  <span className="cw-title-accent">{last}</span>
                </>
              );
            })()}
          </motion.h2>
          <motion.h3
            className="cw-subtitle"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.06 }}
          >
            {t.sectionSubtitle}
          </motion.h3>
          <motion.p
            className="cw-tagline"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.12 }}
          >
            {t.sectionTagline}
          </motion.p>

          <span className="cw-divider" aria-hidden="true">
            <span className="cw-divider-line" />
            <span className="cw-divider-mark">❦</span>
            <span className="cw-divider-line" />
          </span>

          {!hideSanctuaryNote && (
            <>
              <p className="cw-sanctuary-label">{t.sanctuaryLabel}</p>
              <p className="cw-sanctuary-note">{t.sanctuaryNote}</p>
            </>
          )}

          <motion.button
            type="button"
            className="cw-cta"
            data-testid="cw-cta-open"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.2 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="cw-cta-label">{t.cta}</span>
          </motion.button>

          {!isAuthenticated && (
            <p className="cw-account-hint" data-testid="cw-account-hint">
              <Lock size={11} className="cw-account-hint-icon" />
              <span>
                {t.accountHint?.lead || 'Want to keep every chapter you ever capture?'}{' '}
                <Link to="/signup" className="cw-account-hint-link">
                  {t.accountHint?.create || 'Create a free account'}
                </Link>{' '}
                {t.accountHint?.tail || '— your private library lives at'}{' '}
                <Link to="/my-story" className="cw-account-hint-link">/my-story</Link>.
              </span>
            </p>
          )}
        </div>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            className="cw-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeModal}
            data-testid="cw-overlay"
          >
            <motion.div
              className={`cw-scroll ${phase === 'sealing' ? 'is-sealing' : ''}`}
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <span className="cw-rod cw-rod-top" aria-hidden="true" />
              <span className="cw-rod cw-rod-bottom" aria-hidden="true" />

              <button
                type="button"
                className="cw-close"
                onClick={closeModal}
                aria-label={t.modal.close}
                data-testid="cw-close"
              >
                <X size={18} />
              </button>

              <div className="cw-parchment">
                {phase === 'form' || phase === 'sealing' ? (
                  <motion.div
                    className="cw-form-wrap"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: phase === 'sealing' ? 0.55 : 1 }}
                    transition={{ duration: 0.35, delay: phase === 'form' ? 0.3 : 0 }}
                  >
                    {/* Header */}
                    <div className="cw-modal-head">
                      <h2 className="cw-modal-title">{t.modal.heading}</h2>
                      <p className="cw-modal-sub">{t.modal.subheading}</p>
                      {isAuthenticated && (
                        <div className="cw-private-bar" data-testid="cw-private-bar">
                          <Lock size={13} />
                          <span className="cw-private-bar-text">
                            {t.modal.privateBar?.text || 'Saving privately to your library'}
                          </span>
                          {usage && usage.limit != null && (
                            <span className="cw-private-bar-usage" data-testid="cw-usage">
                              {usage.count}/{usage.limit}
                            </span>
                          )}
                        </div>
                      )}
                      <span className="cw-divider cw-divider-sm" aria-hidden="true">
                        <span className="cw-divider-line" />
                        <span className="cw-divider-mark">❦</span>
                        <span className="cw-divider-line" />
                      </span>
                    </div>

                    {/* STEP I */}
                    <section className="cw-step">
                      <div className="cw-step-head">
                        <span className="cw-roman">{t.modal.step1.roman}</span>
                        <h3 className="cw-step-heading">{t.modal.step1.heading}</h3>
                      </div>
                      <label className="cw-input-wrap">
                        <BookMarked size={16} className="cw-input-icon" />
                        <input
                          type="text"
                          className="cw-input"
                          placeholder={t.modal.step1.placeholder}
                          value={bookTitle}
                          onChange={(e) => setBookTitle(e.target.value)}
                          data-testid="cw-book-title"
                          maxLength={300}
                        />
                      </label>
                      <input
                        type="text"
                        className="cw-input cw-input-plain"
                        placeholder={t.modal.step1.authorPlaceholder}
                        value={bookAuthor}
                        onChange={(e) => setBookAuthor(e.target.value)}
                        data-testid="cw-book-author"
                        maxLength={200}
                      />
                      <p className="cw-input-hint">{t.modal.step1.hint}</p>
                    </section>

                    {/* STEP II */}
                    <section className="cw-step">
                      <div className="cw-step-head">
                        <span className="cw-roman">{t.modal.step2.roman}</span>
                        <h3 className="cw-step-heading">{t.modal.step2.heading}</h3>
                      </div>

                      <div className="cw-cards">
                        {/* CARD 1 — Reflection (required, with mic) */}
                        <article className="cw-card cw-card-reflection">
                          <header className="cw-card-head">
                            <Quote size={18} className="cw-card-icon" />
                            <div>
                              <h4 className="cw-card-label">{cards.reflection.label}</h4>
                              <p className="cw-card-subtitle">{cards.reflection.subtitle}</p>
                            </div>
                          </header>
                          <div className="cw-mic-wrap">
                            <button
                              type="button"
                              className={`cw-mic ${listening ? 'is-listening' : ''}`}
                              onClick={toggleListening}
                              aria-pressed={listening}
                              data-testid="cw-mic"
                            >
                              <span className="cw-mic-ripple cw-mic-ripple-1" aria-hidden="true" />
                              <span className="cw-mic-ripple cw-mic-ripple-2" aria-hidden="true" />
                              <Mic size={26} strokeWidth={2.2} />
                            </button>
                            <p className="cw-mic-label">
                              {listening ? cards.reflection.micListening : cards.reflection.micLabel}
                            </p>
                            <p className="cw-mic-hint">{cards.reflection.micHint}</p>
                          </div>
                          <textarea
                            className="cw-textarea"
                            placeholder={cards.reflection.placeholder}
                            value={reflection}
                            onChange={(e) => setReflection(e.target.value)}
                            rows={5}
                            maxLength={2000}
                            data-testid="cw-reflection"
                          />
                          <CharCount value={reflection} max={2000} testId="cw-reflection-count" />
                        </article>

                        {/* CARD 2 — Practice */}
                        <article className="cw-card">
                          <header className="cw-card-head">
                            <Lightbulb size={18} className="cw-card-icon" />
                            <div>
                              <h4 className="cw-card-label">
                                {cards.practice.label}
                                <span className="cw-optional">— {t.modal.step2.optional}</span>
                              </h4>
                              <p className="cw-card-subtitle">{cards.practice.subtitle}</p>
                            </div>
                          </header>
                          <textarea
                            className="cw-textarea cw-textarea-sm"
                            placeholder={cards.practice.placeholder}
                            value={practice}
                            onChange={(e) => setPractice(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            data-testid="cw-practice"
                          />
                        </article>

                        {/* CARD 3 — Passage */}
                        <article className="cw-card">
                          <header className="cw-card-head">
                            <Bookmark size={18} className="cw-card-icon" />
                            <div>
                              <h4 className="cw-card-label">
                                {cards.passage.label}
                                <span className="cw-optional">— {t.modal.step2.optional}</span>
                              </h4>
                              <p className="cw-card-subtitle">{cards.passage.subtitle}</p>
                            </div>
                          </header>
                          <textarea
                            className="cw-textarea cw-textarea-sm"
                            placeholder={cards.passage.placeholder}
                            value={passage}
                            onChange={(e) => setPassage(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            data-testid="cw-passage"
                          />
                        </article>

                        {/* CARD 4 — Revisit */}
                        <article className="cw-card">
                          <header className="cw-card-head">
                            <Repeat size={18} className="cw-card-icon" />
                            <div>
                              <h4 className="cw-card-label">
                                {cards.revisit.label}
                                <span className="cw-optional">— {t.modal.step2.optional}</span>
                              </h4>
                              <p className="cw-card-subtitle">{cards.revisit.subtitle}</p>
                            </div>
                          </header>
                          <textarea
                            className="cw-textarea cw-textarea-sm"
                            placeholder={cards.revisit.placeholder}
                            value={revisit}
                            onChange={(e) => setRevisit(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            data-testid="cw-revisit"
                          />
                        </article>
                      </div>
                    </section>

                    {/* STEP III */}
                    <section className="cw-step">
                      <div className="cw-step-head">
                        <span className="cw-roman">{t.modal.step3.roman}</span>
                        <h3 className="cw-step-heading">{t.modal.step3.heading}</h3>
                      </div>
                      <p className="cw-step-sub">{t.modal.step3.subheading}</p>
                      <input
                        type="text"
                        className="cw-input cw-input-plain"
                        placeholder={t.modal.step3.namePlaceholder}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={120}
                        data-testid="cw-name"
                      />
                      <input
                        type="email"
                        className="cw-input cw-input-plain"
                        placeholder={t.modal.step3.emailPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        maxLength={200}
                        data-testid="cw-email"
                      />
                      <p className="cw-input-hint">{t.modal.step3.emailNote}</p>
                    </section>

                    {error && (
                      <p className="cw-error" data-testid="cw-error" role="alert">{error}</p>
                    )}

                    <div className="cw-submit-wrap">
                      <button
                        type="button"
                        className="cw-submit"
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="cw-submit"
                        style={{ backgroundImage: "url('/seal/icraftz-wax-seal.png')" }}
                      >
                        <span className="cw-submit-scrim" aria-hidden="true" />
                        <span className="cw-submit-label">
                          {submitting ? t.modal.submitting : t.modal.submit}
                        </span>
                      </button>
                      <p className="cw-bottom-tagline">{t.modal.bottomTagline}</p>
                    </div>
                  </motion.div>
                ) : null}

                {phase === 'savePrompt' && (
                  <motion.div
                    className="cw-prompt"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    data-testid="cw-save-prompt"
                  >
                    <Lock size={24} className="cw-prompt-icon" />
                    <h3 className="cw-prompt-heading">{t.modal.savePrompt?.heading || 'Save it forever?'}</h3>
                    <p className="cw-prompt-body">
                      {t.modal.savePrompt?.body ||
                        'Create a free account and this chapter — and every one after it — lives in your private library, always yours to return to.'}
                    </p>
                    <span className="cw-divider cw-divider-sm" aria-hidden="true">
                      <span className="cw-divider-line" />
                      <span className="cw-divider-mark">❦</span>
                      <span className="cw-divider-line" />
                    </span>
                    <div className="cw-prompt-actions">
                      <Link
                        to="/signup"
                        className="cw-prompt-cta-primary"
                        onClick={persistDraft}
                        data-testid="cw-prompt-create"
                      >
                        {t.modal.savePrompt?.create || 'Create free account'}
                      </Link>
                      <Link
                        to="/login"
                        className="cw-prompt-cta-secondary"
                        onClick={persistDraft}
                        data-testid="cw-prompt-signin"
                      >
                        {t.modal.savePrompt?.signin || 'Sign in'}
                      </Link>
                    </div>
                    <button
                      type="button"
                      className="cw-prompt-skip"
                      onClick={doSubmit}
                      disabled={submitting}
                      data-testid="cw-prompt-skip"
                    >
                      {submitting
                        ? (t.modal.submitting || 'Sealing…')
                        : (t.modal.savePrompt?.skip || 'Continue without saving')}
                    </button>
                  </motion.div>
                )}

                {phase === 'capReached' && (
                  <motion.div
                    className="cw-prompt"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    data-testid="cw-cap-reached"
                  >
                    <BookOpen size={24} className="cw-prompt-icon" />
                    <h3 className="cw-prompt-heading">
                      {t.modal.cap?.heading || 'Your shelf is full.'}
                    </h3>
                    <p className="cw-prompt-body">
                      {(t.modal.cap?.body ||
                        'You\'ve sealed 3 of 3 chapters on the free Arambh plan. Free your shelf — delete a chapter you no longer need, or upgrade to keep capturing without limit.')}
                    </p>
                    <span className="cw-divider cw-divider-sm" aria-hidden="true">
                      <span className="cw-divider-line" />
                      <span className="cw-divider-mark">❦</span>
                      <span className="cw-divider-line" />
                    </span>
                    <div className="cw-prompt-actions">
                      <Link to="/my-story" className="cw-prompt-cta-primary" onClick={closeModal} data-testid="cw-cap-mystory">
                        {t.modal.cap?.gotoLibrary || 'Open My Story'}
                      </Link>
                    </div>
                  </motion.div>
                )}

                {phase === 'done' && (
                  <motion.div
                    className="cw-confirm"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                    data-testid="cw-confirm"
                  >
                    <img
                      src="/seal/icraftz-wax-seal.png"
                      alt="iCraftz wax seal"
                      className="cw-wax-seal-img"
                      data-testid="cw-wax-seal-img"
                    />
                    <h3 className="cw-confirm-heading">{t.confirmation.heading}</h3>
                    <p className="cw-confirm-subtitle">{t.confirmation.subtitle}</p>
                    <span className="cw-divider cw-divider-sm" aria-hidden="true">
                      <span className="cw-divider-line" />
                      <span className="cw-divider-mark">❦</span>
                      <span className="cw-divider-line" />
                    </span>
                    <p className="cw-confirm-signoff">{t.confirmation.signoff}</p>
                    {savedPrivate && (
                      <Link
                        to="/my-story"
                        className="cw-confirm-cta"
                        onClick={closeModal}
                        data-testid="cw-confirm-mystory"
                      >
                        {t.confirmation.viewLibrary || 'Open My Story →'}
                      </Link>
                    )}
                    {!savedPrivate && (
                      <div className="cw-confirm-savehint" data-testid="cw-confirm-savehint">
                        <p className="cw-confirm-savehint-lead">
                          <Lock size={12} className="cw-confirm-savehint-icon" />{' '}
                          {t.confirmation.notSavedLead ||
                            "This chapter isn't saved to a library yet."}
                        </p>
                        <p className="cw-confirm-savehint-body">
                          {t.confirmation.notSavedBody ||
                            'Create a free account and every chapter you capture from now on lives in your private library — always yours to return to.'}
                        </p>
                        <Link
                          to="/signup"
                          className="cw-confirm-cta"
                          onClick={closeModal}
                          data-testid="cw-confirm-signup"
                        >
                          {t.confirmation.notSavedCta || 'Create your free account →'}
                        </Link>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
